/**
 * <kik-b2b-hero> — the B2B homepage hero rotator.
 *
 * Cross-fades a stack of background slides while swapping the copy block that
 * belongs to each one. Autoplay pauses while the dot nav is hovered / focused
 * and while the tab is hidden; it never pauses on the whole hero, so moving the
 * pointer across the banner can't leave autoplay stuck.
 *
 * Custom element (not a DOMContentLoaded sweep) so theme-editor section
 * re-renders re-initialise on their own.
 */
class KikB2bHero extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('[data-hero-slide]'));
    this.copies = Array.from(this.querySelectorAll('[data-hero-copy]'));
    this.dots = Array.from(this.querySelectorAll('[data-hero-dot]'));
    if (this.slides.length === 0) return;

    this.index = 0;
    this.timer = null;
    this.interval = Math.max(2000, parseInt(this.dataset.interval, 10) || 5000);

    this.onDotClick = [];
    this.dots.forEach((dot, i) => {
      const handler = () => {
        this.go(i);
        this.start();
      };
      this.onDotClick.push(handler);
      dot.addEventListener('click', handler);
    });

    this.onKeydown = (event) => {
      if (event.key === 'ArrowLeft') this.step(-1);
      if (event.key === 'ArrowRight') this.step(1);
    };
    this.addEventListener('keydown', this.onKeydown);

    this.nav = this.querySelector('[data-hero-nav]');
    if (this.nav) {
      this.pause = () => this.stop();
      this.resume = () => this.start();
      this.nav.addEventListener('mouseenter', this.pause);
      this.nav.addEventListener('mouseleave', this.resume);
      this.nav.addEventListener('focusin', this.pause);
      this.nav.addEventListener('focusout', this.resume);
    }

    this.onVisibility = () => (document.hidden ? this.stop() : this.start());
    document.addEventListener('visibilitychange', this.onVisibility);

    this.go(0);
    if (this.slides.length > 1) this.start();
  }

  disconnectedCallback() {
    this.stop();
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.removeEventListener('keydown', this.onKeydown);
    this.dots.forEach((dot, i) => dot.removeEventListener('click', this.onDotClick[i]));
    if (this.nav) {
      this.nav.removeEventListener('mouseenter', this.pause);
      this.nav.removeEventListener('mouseleave', this.resume);
      this.nav.removeEventListener('focusin', this.pause);
      this.nav.removeEventListener('focusout', this.resume);
    }
  }

  go(next) {
    this.index = (next + this.slides.length) % this.slides.length;
    this.slides.forEach((slide, i) => {
      slide.classList.toggle('is-active', i === this.index);
      slide.setAttribute('aria-hidden', i === this.index ? 'false' : 'true');
    });
    this.copies.forEach((copy, i) => {
      const on = i === this.index;
      copy.classList.toggle('is-active', on);
      copy.hidden = !on;
    });
    this.dots.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === this.index);
      dot.setAttribute('aria-selected', i === this.index ? 'true' : 'false');
    });
  }

  step(direction) {
    this.go(this.index + direction);
    this.start();
  }

  stop() {
    if (this.timer) window.clearInterval(this.timer);
    this.timer = null;
  }

  start() {
    this.stop();
    if (this.slides.length < 2) return;
    this.timer = window.setInterval(() => this.go(this.index + 1), this.interval);
  }
}

if (!customElements.get('kik-b2b-hero')) {
  customElements.define('kik-b2b-hero', KikB2bHero);
}
